<template>
  <div class="min-h-screen bg-gray-50 dark:bg-gray-900">
    <!-- Header -->
    <div class="bg-white dark:bg-gray-800 shadow">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div class="py-6">
          <div class="flex items-center justify-between">
            <div>
              <h1 class="text-2xl font-bold text-gray-900 dark:text-gray-100">Tier Management</h1>
              <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">
                Track your progress and unlock new features
              </p>
            </div>
            <div class="flex items-center space-x-3">
              <span class="text-3xl">{{ currentTierInfo.icon }}</span>
              <div>
                <div class="text-lg font-semibold text-gray-900 dark:text-gray-100">
                  Tier {{ userTier }}
                </div>
                <div class="text-sm text-gray-500 dark:text-gray-400">
                  {{ currentTierInfo.name }}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div class="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <!-- Left Column: Current Status & Progress -->
        <div class="lg:col-span-2 space-y-6">
          <!-- Current Tier Card -->
          <div class="bg-white dark:bg-gray-800 shadow rounded-lg overflow-hidden">
            <div class="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
              <h2 class="text-lg font-medium text-gray-900 dark:text-gray-100">Current Status</h2>
            </div>
            <div class="p-6">
              <div class="flex items-center justify-between mb-6">
                <div class="flex items-center space-x-4">
                  <div
                    class="w-16 h-16 rounded-full bg-gradient-to-r from-blue-500 to-purple-600 flex items-center justify-center text-2xl"
                  >
                    {{ currentTierInfo.icon }}
                  </div>
                  <div>
                    <h3 class="text-xl font-semibold text-gray-900 dark:text-gray-100">
                      {{ currentTierInfo.name }}
                    </h3>
                    <p class="text-gray-500 dark:text-gray-400">
                      {{ currentTierInfo.description }}
                    </p>
                  </div>
                </div>
                <div class="text-right">
                  <div class="text-2xl font-bold text-gray-900 dark:text-gray-100">
                    {{ userTier }}/7
                  </div>
                  <div class="text-sm text-gray-500 dark:text-gray-400">Tier Level</div>
                </div>
              </div>

              <!-- Overall Progress -->
              <div class="mb-6">
                <div class="flex justify-between text-sm text-gray-600 dark:text-gray-300 mb-2">
                  <span>Overall Progress</span>
                  <span>{{ Math.round((userTier / 7) * 100) }}%</span>
                </div>
                <div class="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-3">
                  <div
                    class="bg-gradient-to-r from-blue-500 to-purple-600 h-3 rounded-full transition-all duration-500"
                    :style="{ width: `${(userTier / 7) * 100}%` }"
                  />
                </div>
              </div>

              <!-- Current Tier Benefits -->
              <div>
                <h4 class="text-sm font-medium text-gray-900 dark:text-gray-100 mb-3">
                  Your Current Benefits
                </h4>
                <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div
                    v-for="benefit in currentTierInfo.benefits"
                    :key="benefit"
                    class="flex items-center space-x-2"
                  >
                    <CheckIcon class="h-4 w-4 text-green-500 flex-shrink-0" />
                    <span class="text-sm text-gray-600 dark:text-gray-300">{{ benefit }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Next Tier Requirements -->
          <div
            v-if="nextTierInfo"
            class="bg-white dark:bg-gray-800 shadow rounded-lg overflow-hidden"
          >
            <div class="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
              <h2 class="text-lg font-medium text-gray-900 dark:text-gray-100">
                Next: {{ nextTierInfo.name }}
              </h2>
            </div>
            <div class="p-6">
              <div class="flex items-center space-x-4 mb-6">
                <div
                  class="w-12 h-12 rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center text-xl"
                >
                  {{ nextTierInfo.icon }}
                </div>
                <div>
                  <h3 class="text-lg font-semibold text-gray-900 dark:text-gray-100">
                    {{ nextTierInfo.name }}
                  </h3>
                  <p class="text-gray-500 dark:text-gray-400">
                    {{ nextTierInfo.description }}
                  </p>
                </div>
              </div>

              <!-- Requirements Checklist -->
              <div class="space-y-4">
                <h4 class="text-sm font-medium text-gray-900 dark:text-gray-100">
                  Requirements to Unlock
                </h4>

                <div
                  v-for="requirement in nextTierInfo.requirements"
                  :key="requirement.key"
                  class="flex items-center justify-between p-4 border border-gray-200 dark:border-gray-700 rounded-lg"
                >
                  <div class="flex items-center space-x-3">
                    <div
                      class="w-6 h-6 rounded-full flex items-center justify-center"
                      :class="
                        requirement.completed
                          ? 'bg-green-100 dark:bg-green-900'
                          : 'bg-gray-100 dark:bg-gray-700'
                      "
                    >
                      <CheckIcon
                        v-if="requirement.completed"
                        class="h-4 w-4 text-green-600 dark:text-green-400"
                      />
                      <span v-else class="w-2 h-2 bg-gray-400 dark:bg-gray-500 rounded-full" />
                    </div>
                    <div>
                      <div class="text-sm font-medium text-gray-900 dark:text-gray-100">
                        {{ requirement.title }}
                      </div>
                      <div class="text-xs text-gray-500 dark:text-gray-400">
                        {{ requirement.description }}
                      </div>
                    </div>
                  </div>
                  <button
                    v-if="!requirement.completed && requirement.actionable"
                    class="px-3 py-1 text-sm font-medium rounded-md border border-transparent"
                    :class="
                      requirement.actionType === 'verify'
                        ? 'bg-blue-600 text-white hover:bg-blue-700'
                        : 'bg-gray-600 text-white hover:bg-gray-700'
                    "
                    @click="handleRequirementAction(requirement)"
                  >
                    {{ requirement.actionLabel }}
                  </button>
                  <span
                    v-else-if="requirement.completed"
                    class="px-3 py-1 text-sm font-medium text-green-600 dark:text-green-400"
                  >
                    ✓ Complete
                  </span>
                  <span
                    v-else
                    class="px-3 py-1 text-sm font-medium text-gray-500 dark:text-gray-400"
                  >
                    {{ requirement.statusText }}
                  </span>
                </div>
              </div>

              <!-- Progress towards next tier -->
              <div class="mt-6">
                <div class="flex justify-between text-sm text-gray-600 dark:text-gray-300 mb-2">
                  <span>Progress to {{ nextTierInfo.name }}</span>
                  <span>{{ nextTierProgress }}%</span>
                </div>
                <div class="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                  <div
                    class="bg-blue-500 h-2 rounded-full transition-all duration-500"
                    :style="{ width: `${nextTierProgress}%` }"
                  />
                </div>
              </div>
            </div>
          </div>

          <!-- Maximum Tier Reached -->
          <div
            v-else
            class="bg-gradient-to-r from-purple-500 to-pink-500 rounded-lg p-6 text-white"
          >
            <div class="flex items-center space-x-4">
              <div
                class="w-12 h-12 rounded-full bg-white bg-opacity-20 flex items-center justify-center text-2xl"
              >
                🏆
              </div>
              <div>
                <h3 class="text-xl font-semibold">Congratulations!</h3>
                <p class="text-purple-100">
                  You've reached the maximum tier level. Enjoy all premium features!
                </p>
              </div>
            </div>
          </div>
        </div>

        <!-- Right Column: All Tiers Overview -->
        <div class="space-y-6">
          <!-- Quick Actions -->
          <div class="bg-white dark:bg-gray-800 shadow rounded-lg overflow-hidden">
            <div class="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
              <h2 class="text-lg font-medium text-gray-900 dark:text-gray-100">Quick Actions</h2>
            </div>
            <div class="p-6 space-y-3">
              <button
                v-if="canVerifyEmail"
                class="w-full flex items-center justify-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                @click="showEmailVerificationModal = true"
              >
                <EnvelopeIcon class="h-4 w-4 mr-2" />
                Verify Email
              </button>

              <button
                v-if="canVerifyPhone"
                class="w-full flex items-center justify-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                @click="showPhoneVerificationModal = true"
              >
                <PhoneIcon class="h-4 w-4 mr-2" />
                Verify Phone
              </button>

              <button
                v-if="canSetPreferences"
                class="w-full flex items-center justify-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-purple-600 hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
                @click="$router.push('/profile/preferences')"
              >
                <CogIcon class="h-4 w-4 mr-2" />
                Set Preferences
              </button>

              <button
                v-if="canAddPayment"
                class="w-full flex items-center justify-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                @click="showPaymentModal = true"
              >
                <CreditCardIcon class="h-4 w-4 mr-2" />
                Add Payment Method
              </button>
            </div>
          </div>

          <!-- All Tiers Overview -->
          <div class="bg-white dark:bg-gray-800 shadow rounded-lg overflow-hidden">
            <div class="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
              <h2 class="text-lg font-medium text-gray-900 dark:text-gray-100">All Tiers</h2>
            </div>
            <div class="p-6">
              <div class="space-y-4">
                <div
                  v-for="tier in allTiers"
                  :key="tier.level"
                  class="flex items-center space-x-3 p-3 rounded-lg"
                  :class="
                    tier.level === userTier
                      ? 'bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800'
                      : tier.level < userTier
                        ? 'bg-green-50 dark:bg-green-900/20'
                        : 'bg-gray-50 dark:bg-gray-700/50'
                  "
                >
                  <div
                    class="w-8 h-8 rounded-full flex items-center justify-center text-sm"
                    :class="
                      tier.level <= userTier
                        ? 'bg-green-100 dark:bg-green-900 text-green-600 dark:text-green-400'
                        : 'bg-gray-100 dark:bg-gray-700 text-gray-400'
                    "
                  >
                    <CheckIcon v-if="tier.level <= userTier" class="h-4 w-4" />
                    <span v-else>{{ tier.level }}</span>
                  </div>
                  <div class="flex-1">
                    <div class="flex items-center space-x-2">
                      <span class="text-lg">{{ tier.icon }}</span>
                      <span class="text-sm font-medium text-gray-900 dark:text-gray-100">
                        {{ tier.name }}
                      </span>
                      <span
                        v-if="tier.level === userTier"
                        class="px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200 rounded-full"
                      >
                        Current
                      </span>
                    </div>
                    <div class="text-xs text-gray-500 dark:text-gray-400">
                      {{ tier.shortDescription }}
                    </div>
                  </div>
                  <div class="text-xs text-gray-400 dark:text-gray-500">
                    {{ tier.rateLimit }}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Verification Modals -->
    <EmailVerificationModal
      :is-open="showEmailVerificationModal"
      @close="showEmailVerificationModal = false"
      @verified="handleEmailVerified"
    />

    <PhoneVerificationModal
      :is-open="showPhoneVerificationModal"
      @close="showPhoneVerificationModal = false"
      @verified="handlePhoneVerified"
    />

    <!-- Payment Modal Placeholder -->
    <div
      v-if="showPaymentModal"
      class="fixed inset-0 z-50 overflow-y-auto"
      aria-labelledby="modal-title"
      role="dialog"
      aria-modal="true"
    >
      <div
        class="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0"
      >
        <div
          class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
          @click="showPaymentModal = false"
        />
        <div
          class="relative inline-block align-bottom bg-white dark:bg-gray-800 rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6"
        >
          <div class="text-center">
            <h3 class="text-lg leading-6 font-medium text-gray-900 dark:text-gray-100">
              Payment Integration
            </h3>
            <p class="mt-2 text-sm text-gray-500 dark:text-gray-400">
              Payment method collection will be available soon. This feature is currently under
              development.
            </p>
            <div class="mt-5">
              <button
                type="button"
                class="inline-flex justify-center w-full rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:text-sm"
                @click="showPaymentModal = false"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import { useAuthStore } from '@/stores/auth';
import {
  CheckIcon,
  EnvelopeIcon,
  PhoneIcon,
  CogIcon,
  CreditCardIcon,
} from '@heroicons/vue/24/outline';
import EmailVerificationModal from '@/components/EmailVerificationModal.vue';
import PhoneVerificationModal from '@/components/PhoneVerificationModal.vue';

defineOptions({
  name: 'TierProfileView',
});

const router = useRouter();
const authStore = useAuthStore();

// State
const showEmailVerificationModal = ref(false);
const showPhoneVerificationModal = ref(false);
const showPaymentModal = ref(false);
// Computed
const userTier = computed(() => authStore.user?.tier || 0);

const allTiers = computed(() => [
  {
    level: 0,
    name: 'Basic Access',
    icon: '👋',
    shortDescription: 'Telegram URL verification',
    rateLimit: '10/min',
    description: 'Basic access to the platform with Telegram verification',
    benefits: ['Basic bot access', 'Standard support', 'Community features'],
  },
  {
    level: 1,
    name: 'Verified Contact',
    icon: '📧',
    shortDescription: 'Email + Phone verified',
    rateLimit: '20/min',
    description: 'Enhanced access with verified contact information',
    benefits: ['Email notifications', 'Phone support', 'Priority assistance', 'Enhanced security'],
  },
  {
    level: 2,
    name: 'Personalized',
    icon: '⚙️',
    shortDescription: 'Preferences configured',
    rateLimit: '50/min',
    description: 'Personalized experience with custom settings',
    benefits: [
      'Custom preferences',
      'Personalized dashboard',
      'Advanced settings',
      'Custom themes',
    ],
  },
  {
    level: 3,
    name: 'Multi-Bot User',
    icon: '🤖',
    shortDescription: '3+ active bots',
    rateLimit: '100/min',
    description: 'Power user with multiple bot configurations',
    benefits: ['Multiple bots', 'Advanced automation', 'Bulk operations', 'Enhanced limits'],
  },
  {
    level: 4,
    name: 'Identity Verified',
    icon: '🆔',
    shortDescription: 'ID/Passport verified',
    rateLimit: '200/min',
    description: 'Verified identity for enhanced security and trust',
    benefits: [
      'Identity verification',
      'Organization features',
      'Advanced security',
      'Compliance features',
    ],
  },
  {
    level: 5,
    name: 'Payment Ready',
    icon: '💳',
    shortDescription: 'Payment method added',
    rateLimit: '500/min',
    description: 'Ready for premium features and services',
    benefits: [
      'Premium features',
      'Payment processing',
      'Subscription management',
      'Enterprise support',
    ],
  },
  {
    level: 6,
    name: 'Proven Spender',
    icon: '💰',
    shortDescription: '$100+ spent',
    rateLimit: '1000/min',
    description: 'Established customer with spending history',
    benefits: ['API access', 'Advanced integrations', 'Priority support', 'Beta features'],
  },
  {
    level: 7,
    name: 'Enterprise',
    icon: '🏢',
    shortDescription: '$500+ spent',
    rateLimit: 'Unlimited',
    description: 'Enterprise-level access and features',
    benefits: ['White labeling', 'Custom integrations', 'Dedicated support', 'SLA guarantees'],
  },
]);

const currentTierInfo = computed(() => allTiers.value[userTier.value] || allTiers.value[0]);

const nextTierInfo = computed(() => {
  if (userTier.value >= 7) return null;

  const nextTier = allTiers.value[userTier.value + 1];
  if (!nextTier) return null;

  const requirements = getRequirementsForTier(userTier.value + 1);

  return {
    ...nextTier,
    requirements,
  };
});

const nextTierProgress = computed(() => {
  if (!nextTierInfo.value) return 100;

  const completedRequirements = nextTierInfo.value.requirements.filter(req => req.completed).length;
  const totalRequirements = nextTierInfo.value.requirements.length;

  return totalRequirements > 0 ? Math.round((completedRequirements / totalRequirements) * 100) : 0;
});

const canVerifyEmail = computed(() => {
  return userTier.value === 0 && !authStore.user?.emailVerified;
});

const canVerifyPhone = computed(() => {
  return userTier.value <= 1 && !authStore.user?.phoneVerified;
});

const canSetPreferences = computed(() => {
  return userTier.value === 1 && !hasSetPreferences.value;
});

const canAddPayment = computed(() => {
  return userTier.value >= 2 && userTier.value <= 4 && !hasPaymentMethod.value;
});

const hasSetPreferences = computed(() => {
  // This would check if user has set their preferences
  // For now, assume false for demonstration
  return false;
});

const hasPaymentMethod = computed(() => {
  // This would check if user has added a payment method
  // For now, assume false for demonstration
  return false;
});

// Methods
function getRequirementsForTier(tier: number) {
  const user = authStore.user;

  switch (tier) {
    case 1:
      return [
        {
          key: 'email_verified',
          title: 'Email Verification',
          description: 'Verify your email address to receive notifications',
          completed: user?.emailVerified || false,
          actionable: !user?.emailVerified,
          actionType: 'verify',
          actionLabel: 'Verify Email',
          statusText: user?.emailVerified ? 'Verified' : 'Pending',
        },
        {
          key: 'phone_verified',
          title: 'Phone Verification',
          description: 'Verify your phone number for additional security',
          completed: user?.phoneVerified || false,
          actionable: !user?.phoneVerified,
          actionType: 'verify',
          actionLabel: 'Verify Phone',
          statusText: user?.phoneVerified ? 'Verified' : 'Pending',
        },
      ];

    case 2:
      return [
        {
          key: 'preferences_set',
          title: 'Set Preferences',
          description: 'Configure your personalized settings and preferences',
          completed: hasSetPreferences.value,
          actionable: !hasSetPreferences.value,
          actionType: 'configure',
          actionLabel: 'Set Preferences',
          statusText: hasSetPreferences.value ? 'Configured' : 'Not Set',
        },
      ];

    case 3:
      return [
        {
          key: 'multiple_bots',
          title: 'Create 3+ Bots',
          description: 'Create and configure at least 3 active bots',
          completed: false, // This would check actual bot count
          actionable: false,
          actionType: 'usage',
          actionLabel: 'Create Bots',
          statusText: '0/3 bots created',
        },
      ];

    case 4:
      return [
        {
          key: 'identity_verified',
          title: 'Identity Verification',
          description: 'Verify your identity with government-issued ID',
          completed: false, // This would check verification status
          actionable: true,
          actionType: 'verify',
          actionLabel: 'Verify Identity',
          statusText: 'Pending verification',
        },
      ];

    case 5:
      return [
        {
          key: 'payment_method',
          title: 'Add Payment Method',
          description: 'Add a valid payment method to your account',
          completed: hasPaymentMethod.value,
          actionable: !hasPaymentMethod.value,
          actionType: 'configure',
          actionLabel: 'Add Payment',
          statusText: hasPaymentMethod.value ? 'Added' : 'Not Added',
        },
      ];

    case 6:
      return [
        {
          key: 'spending_100',
          title: 'Spend $100+',
          description: 'Make purchases totaling $100 or more',
          completed: false, // This would check actual spending
          actionable: false,
          actionType: 'usage',
          actionLabel: 'Make Purchases',
          statusText: '$0/$100 spent',
        },
      ];

    case 7:
      return [
        {
          key: 'spending_500',
          title: 'Spend $500+',
          description: 'Make purchases totaling $500 or more',
          completed: false, // This would check actual spending
          actionable: false,
          actionType: 'usage',
          actionLabel: 'Make Purchases',
          statusText: '$0/$500 spent',
        },
      ];

    default:
      return [];
  }
}

function handleRequirementAction(requirement: any) {
  switch (requirement.key) {
    case 'email_verified':
      showEmailVerificationModal.value = true;
      break;
    case 'phone_verified':
      showPhoneVerificationModal.value = true;
      break;
    case 'preferences_set':
      router.push('/profile/preferences');
      break;
    case 'payment_method':
      showPaymentModal.value = true;
      break;
    case 'identity_verified':
      // Would open identity verification modal
      console.log('Identity verification coming soon');
      break;
    default:
      console.log('Action not implemented:', requirement.key);
  }
}

async function handleEmailVerified(email: string) {
  showEmailVerificationModal.value = false;

  try {
    await authStore.refreshUserData();
    console.log('Email verified successfully:', email);
  } catch (error) {
    console.error('Failed to refresh user data:', error);
  }
}

async function handlePhoneVerified(phoneNumber: string) {
  showPhoneVerificationModal.value = false;

  try {
    await authStore.refreshUserData();
    console.log('Phone verified successfully:', phoneNumber);
  } catch (error) {
    console.error('Failed to refresh user data:', error);
  }
}

// Lifecycle
onMounted(async () => {
  // Load any additional tier data if needed
  if (!authStore.isAuthenticated) {
    router.push('/auth');
  }
});
</script>

<style scoped>
/* Custom animations for progress bars */
@keyframes pulse {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.5;
  }
}

.animate-pulse {
  animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
}

/* Smooth transitions for all interactive elements */
.transition-all {
  transition-property: all;
  transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
  transition-duration: 300ms;
}

/* Custom gradient animations */
@keyframes gradient {
  0% {
    background-position: 0% 50%;
  }
  50% {
    background-position: 100% 50%;
  }
  100% {
    background-position: 0% 50%;
  }
}

.bg-gradient-to-r {
  background-size: 200% 200%;
  animation: gradient 3s ease infinite;
}
</style>
